import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian11() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "11");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="Syu_E-O-sU4"
        title="Kanaa Kandenadi — Vidyasagar · Parthiban Kanavu (2003)"
        caption="Count 1-e-and-a with every beat. The bass line is your subdivision test. If you can notate it accurately, you can read and write any groove."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/11-kanaa-kandenadi-page1.png"
        caption="Jason's chart — the 16th-note count (1 e & a, 2 e & a …) with the bass line's hit points circled — 1, the 'a' of 1, the '&' of 2, the '&' of 3, and 4 — the two-bar bass rhythm with its swaras (Sa, upper Sa, Ni, Pa), and a reminder to beam 8th notes rather than write separate flags."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/11-kanaa-kandenadi-page2.png"
        caption="Page 2 — 16th-note rhythm cells built from dotted 8ths, 16ths and rests (marked by where they land: &, e, a), a triplet for comparison, and a 4/4 bar that uses them."
      />

      <SISCard label="The big idea" title="Subdivide by 4 — the groove you feel before you can name it">
        <p>
          Every musician has played grooves they can feel but couldn't notate. <em>Kanaa Kandenadi</em> fixes that. Vidyasagar's two-bar bass line, on E, is an ideal notation study: it uses very few notes, it repeats, and it mixes 16th-note hits, rests and one or two ghost notes — the mixture that makes a groove feel <em>human</em> rather than mechanical.
        </p>
        <p>
          The crucial skill: <strong>subdividing by 4</strong>. In 4/4 time, each beat divides into two 8th notes (1-and) — but 16th-note grooves require dividing each beat into four: <strong>1-e-and-a</strong>. That means 16 individual slots per bar where a note, a ghost, or a rest can live. Most beginners can play the loud beats but rush or skip the in-between subdivisions. Vidyasagar's bass line forces you to feel all 16.
        </p>
        <p>
          Jason: <em>"Your first exercise would be to notate the bassline. It's a very, very easy bassline, isn't it?"</em> It sounds easy — but writing down where every ghost note falls, and playing it back from your notation, is where the learning happens.
        </p>
      </SISCard>

      <SISCard label="Ghost notes decoded" title="The 'whisper beats' that make grooves breathe">
        <p>
          A <strong>ghost note</strong> is a note played very quietly — so quietly that it doesn't register as a pitched note to the listener, but its absence would make the groove feel stiff. In notation, ghost notes are marked with an X notehead or written in parentheses. In performance, they're a slight touch of the key with no weight — a whisper, not a word.
        </p>
        <p>
          Ghost notes serve two purposes: (1) they fill the subdivisions between the loud beats, keeping the physical motion of the hand continuous; (2) they give the listener a subtle sense of rhythmic density that feels like groove without actually adding prominent notes. This is why a great drummer's groove sounds "full" even when the notation looks sparse — the ghost notes are there, barely audible, doing the work.
        </p>
        <p>
          Jason hears one or two ghost notes in this bass line and shows how to notate them. The main hits, as his chart circles them, are beat 1, the "a" of 1, the "and" of 2, the "and" of 3, and beat 4. Clap only those and the groove sounds skeletal; add the in-between touches and it suddenly has body.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="Under the bass line · E minor — Kharaharapriya (Dorian)"
        caption="The chord barely moves while the bass pattern loops — Jason: chordally it's just E minor, and he suggests Kharaharapriya (Dorian) chords for the keyboard. The bass uses Sa, Ni and Pa (E, D and B here) plus the upper Sa. Static harmony is the frame; the active bass is the picture."
        chords={[
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3","E4"] },
          { bar: true },
          { chord: "Em", rn: "i", function: "Tonic", pitches: ["E3","G3","B3","E4"] },
        ]}
      />

      <SISCard label="Writing it down" title="The notation process — step by step">
        <p>
          A step-by-step method for transcribing a 16th-note bass line:
        </p>
        <ol className="mt-2 space-y-2 pl-4 text-[15px]">
          <li><strong>1. Draw the grid first.</strong> Before you hear the music, draw four beats, each divided into four slots: 1-e-and-a / 2-e-and-a / 3-e-and-a / 4-e-and-a. That's 16 boxes per bar.</li>
          <li><strong>2. Mark the loud hits.</strong> Listen once and place a capital X in every box where you hear a prominent note. Don't worry about pitch yet — just the timing.</li>
          <li><strong>3. Listen again for the ghosts.</strong> Ghost notes sound like a soft "thunk" or barely-voiced note between the loud ones. Mark them with a lowercase x or parentheses (x).</li>
          <li><strong>4. Everything else is a rest.</strong> Any empty box gets a 16th-note rest. Now your notation has accounted for all 16 subdivisions.</li>
          <li><strong>5. Add pitch.</strong> Now go back and write the actual note names — or swaras, as Jason allows. This bass line uses very few: Sa, upper Sa, Ni and Pa (E, E, D and B in this key).</li>
          <li><strong>6. Play it back.</strong> If your version doesn't sound like the original, you missed something — listen again and revise.</li>
        </ol>
      </SISCard>

      <SISInfluence
        artists={[
          "James Brown — 'Funky Drummer' (the canonical ghost-note groove study)",
          "Stevie Wonder — 'Superstition' (clavinet 16th-note riff, same grid concept)",
          "Snarky Puppy — 'What About Me?' (modern ghost-note bass playing)",
          "Carnatic Konnakol (the art of rhythmic syllables — ta-ka-di-mi = 1-e-and-a)",
        ]}
      >
        <p>
          Carnatic Konnakol (vocal percussion) uses syllable groups to feel subdivisions physically: <em>ta-ka-di-mi</em> for 4-subdivision (= 1-e-and-a in Western notation), <em>ta-ka</em> for 2-subdivision (= 1-and). Practice saying ta-ka-di-mi aloud while tapping your foot on beats 1 and 3. Funk drummers such as Clyde Stubblefield, on James Brown's "Funky Drummer", work from the same physical sense of subdivision — same principle, different vocabulary.
        </p>
      </SISInfluence>

      <SISCallout label="Practice drill — clap before you play">
        <p>
          Before touching the piano, clap the bass line from this song. Use two different clap intensities: a full-palm clap for loud notes and a finger-tap on your thigh for ghost notes. Count aloud: <em>"1 – e – AND – a – 2 – e – AND – a..."</em> Loud on the beats you identified; soft tap on the ghost positions. When your clapping sounds like the bass line, you're ready to play it. This physical-before-instrumental approach catches rhythmic errors that your brain would otherwise paper over.
        </p>
      </SISCallout>

      <SISTakeaway>
        <p>
          Notating rhythm is the skill most musicians skip — and its absence is the reason why many players can copy a groove but can't create one from scratch or explain what they're playing. When you can write down exactly where every ghost note falls in a 2-bar 16th-note pattern, you have <strong>rhythmic literacy</strong>: the ability to read, write, and compose groove the same way a classically-trained musician reads pitch. Carnatic training builds this literacy through tala (rhythmic framework) and Konnakol (vocal rhythm). This lesson is where South Indian Songbook students begin to build that same foundation.
        </p>
      </SISTakeaway>
    </LessonShell>
  );
}
